/**
 * Every call the app makes goes to its own origin, and the Worker forwards it.
 *
 * Nothing here knows the API's address, and nothing here carries a token: the
 * session is a `__Host-` cookie on this origin, so `credentials: "same-origin"`
 * is the whole of the auth layer on this side. That is also why the app may not
 * be served from anywhere but the console's own hostname — a `__Host-` cookie
 * cannot travel (design note 0001 §3).
 */

import type { z } from "zod";
import { errorEnvelope } from "@sitebrew/contracts/v1";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;

  constructor(status: number, code: string | undefined, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

/**
 * An answer that came back `2xx` and did not match its contract.
 *
 * Thrown rather than passed on (#290: the console `parse`s and fails closed),
 * and **not** an `ApiError`: the screens read an `ApiError` as the API's own
 * refusal — a `404` is "no such site", a `401` is "nobody is signed in" — and
 * this is neither. It is the API answering something this build of the console
 * cannot read, and the only honest screen for that is the one that says so.
 * The endpoint and the first issue's path are in the message, because "invalid
 * response" with no address is a bug report nobody can act on.
 */
export class ContractError extends Error {
  readonly endpoint: string;
  readonly issues: ReadonlyArray<{ path: string; message: string }>;

  constructor(endpoint: string, issues: ReadonlyArray<{ path: string; message: string }>) {
    const first = issues[0];
    super(
      `${endpoint} answered a body the console cannot read` +
        (first ? ` (${first.path || "(root)"}: ${first.message})` : ""),
    );
    this.name = "ContractError";
    this.endpoint = endpoint;
    this.issues = issues;
  }
}

type Options = { method?: string; body?: unknown; signal?: AbortSignal };

/**
 * A JSON call to `/v1`, with the schema its answer must parse against.
 *
 * The schema is a parameter rather than a type argument because a type
 * argument checks nothing at run time — `api<{ site: Site }>` was a promise
 * the console made to itself. Every `2xx` body goes through `schema.parse`
 * (`@sitebrew/contracts/v1`), so a field the API renames fails here, naming
 * the endpoint, instead of three screens up as `undefined`.
 *
 * A non-2xx answer becomes an `ApiError` carrying the status **and** the API's
 * own `error` code, because the screens branch on the code and only fall back
 * to the status. Losing the code here is what turns "that slug is taken" into
 * "something went wrong" three layers up.
 */
export async function api<S extends z.ZodType>(
  path: string,
  schema: S,
  options: Options = {},
): Promise<z.output<S>> {
  const response = await fetch(`/v1${path}`, {
    method: options.method ?? "GET",
    credentials: "same-origin",
    headers: options.body === undefined ? undefined : { "content-type": "application/json" },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  });

  const endpoint = `${options.method ?? "GET"} /v1${path}`;

  // `204` is an answer with nothing in it, and the schema says whether nothing
  // was expected: `noContent` parses `undefined`, every other schema refuses it.
  if (response.status === 204) return checked(endpoint, schema, undefined);

  // A body is expected, but an error page from a hop in between is not JSON.
  // Failing to parse must read as the failure it is, not as a successful empty
  // answer.
  const payload = await response.json().catch(() => undefined);

  if (!response.ok) {
    /*
     * The API has one error shape for its whole surface (design note 0001 §6):
     * `{ error: { code, message } }`. This used to read a flat
     * `{ error, message }`, which is not a shape anything sends — so `code`
     * arrived as the *object* and no comparison against it could ever be true,
     * and every message fell through to the generic sentence below, discarding
     * the API's own wording.
     *
     * Nothing failed, because until this change the console met exactly one
     * error: the `401` from `/me`, which is decided by status and never reads
     * either field. The first screen to branch on a code would have been the
     * first to find out — which is why `test/api-error.test.tsx` pins the shape
     * against a recorded answer rather than against this file.
     */
    const envelope = errorEnvelope.safeParse(payload);
    throw new ApiError(
      response.status,
      envelope.success ? envelope.data.error.code : undefined,
      envelope.success ? envelope.data.error.message : `The request failed (${response.status})`,
    );
  }

  return checked(endpoint, schema, payload);
}

function checked<S extends z.ZodType>(endpoint: string, schema: S, payload: unknown): z.output<S> {
  const verdict = schema.safeParse(payload);
  if (verdict.success) return verdict.data;
  throw new ContractError(
    endpoint,
    verdict.error.issues.map((issue) => ({ path: issue.path.map(String).join("."), message: issue.message })),
  );
}
